import { useSearchParams } from 'react-router-dom';

type QueryParamsType<T extends string> = {
  [K in T]?: string;
};

export function useQueryParams<T extends string>(params: readonly T[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getParams = (): QueryParamsType<T> => {
    return params.reduce(
      (acc, paramName) => {
        acc[paramName] = searchParams.get(paramName) || undefined;
        return acc;
      },
      {} as QueryParamsType<T>,
    );
  };

  // setParams({ first: 'value', second: 'value' }) => '?first=value&second=value'
  // setParams({ first: 'value' }, true) => '?first=value'
  // setParams({}, true) => ''
  const setParams = (paramValues?: Partial<QueryParamsType<T>>, reset = false) => {
    // Do not use setSearchParams((prevParams)=>{})
    // If quick successive calls are made then prevParams will not be updated between the calls
    // and only the final values will persist
    // Link to fix: https://github.com/remix-run/react-router/issues/9757#issuecomment-1358336279
    const newParams = new URLSearchParams(window.location.search);

    if (reset) {
      for (const [_, value] of Object.entries(params)) {
        newParams.delete(value);
      }
    }

    if (paramValues) {
      for (const [key, value] of Object.entries(paramValues)) {
        if (value !== undefined && value !== '') {
          newParams.set(key, String(value));
        } else {
          newParams.delete(key);
        }
      }
    }

    newParams;

    setSearchParams(newParams, { replace: true });
  };

  return {
    queryParams: getParams(),
    setQueryParams: setParams,
  };
}
